import { Box, ButtonV2, FormControl } from '@withjoy/joykit';
import { Search } from '@withjoy/joykit/icons';
import React, { useState } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useSearchFormConfig } from '../useSearchFormConfig';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { animationTransition } from '@shared/utils/animationTransition';
import { useFormikContext } from 'formik';
import { isValidCoordinates } from '@shared/core/places';

// Button expands on field focus with smooth animation, collapses after blur delay
const COLLAPSED_VERSION_WIDTH = pxToRem(40); // Collapsed: shows search icon only

export const SubmitButton = ({ backgroundColor }: { backgroundColor?: string }) => {
  const { tq } = useTranslation('locationDatesOccupants');
  const isMobileOrTablet = useIsMobileOrTablet();
  const { isOpenedFormView, isCompactVersion, isAnyFieldFocused } = useSearchFormConfig();

  const [isHovered, setIsHovered] = useState(false);

  const { values } = useFormikContext<{ location: { latitude?: number; longitude?: number } }>();
  const isDisabled = !isValidCoordinates({ latitude: values.location.latitude, longitude: values.location.longitude });

  if (isOpenedFormView) {
    return (
      <FormControl
        padding={5}
        background="white"
        bottom={0}
        position="sticky"
        width="100%"
        borderTop="solid 1px"
        borderColor="mono3"
        zIndex="sticky"
      >
        <ButtonV2
          type="submit"
          disabled={isDisabled}
          width="100%"
          variant="solid"
          shape="rounded"
          intent="primary"
          backgroundColor={backgroundColor}
          _hover={backgroundColor ? { backgroundColor } : undefined}
          borderRadius={pxToRem(12)}
        >
          {tq`Search`}
        </ButtonV2>
      </FormControl>
    );
  }

  const isExpanded = isAnyFieldFocused || isHovered;
  const shouldShowText = isMobileOrTablet || isCompactVersion || (!isMobileOrTablet && isExpanded);
  return (
    <FormControl
      width={isCompactVersion ? '100%' : { _: '100%', md: 'fit-content' }}
      overrides={{
        ContentContainer: {
          props: {
            display: 'flex',
            justifyContent: 'flex-end',
          },
        },
      }}
    >
      <ButtonV2
        type="submit"
        disabled={isDisabled}
        variant="solid"
        intent={'primary'}
        backgroundColor={backgroundColor}
        _hover={backgroundColor ? { backgroundColor } : undefined}
        shape={'rounded'}
        width={isCompactVersion ? '100%' : { _: '100%', md: isExpanded ? '100%' : COLLAPSED_VERSION_WIDTH }}
        height={pxToRem(40)}
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={shouldShowText ? 2 : 0}
        paddingLeft={3}
        paddingRight={shouldShowText ? pxToRem(10) : 3}
        transition={animationTransition('width', 'gap', 'padding-left', 'padding-right', {
          duration: '700ms',
        })}
        aria-label={tq`Search`}
        overflow="hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {!isMobileOrTablet && (
          <Box
            transform={shouldShowText ? 'scale(0.8)' : 'scale(1)'}
            transition={animationTransition('transform', {
              duration: '700ms',
            })}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Search size={24} />
          </Box>
        )}
        <Box
          as="span"
          width={shouldShowText ? (isCompactVersion ? 'auto' : '100%') : 0}
          opacity={shouldShowText ? 1 : 0}
          typographyVariant={!isMobileOrTablet && isExpanded ? 'button1' : 'button2'}
          fontWeight={!isMobileOrTablet && isExpanded ? 500 : 600}
          transition={animationTransition('width', 'opacity', {
            duration: '700ms',
          })}
          whiteSpace="nowrap"
        >
          {tq`Search`}
        </Box>
      </ButtonV2>
    </FormControl>
  );
};
